import React, { useEffect, useState } from 'react';
import { getConfig } from '../config';

type Row = {
  userName: string;
  content: string | null;
  contentAudio: string | null;
  transcript: string | null;
  type: string;
  drawingAttached: string;
  tags: string | null;
  userWorkspaces: string;
  createdAt: string;
};

type Workspace = { id: number; name: string };

const AnalyticsComments: React.FC = () => {
  const { apiBase } = getConfig();
  const [rows, setRows] = useState<Row[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [workspaceId, setWorkspaceId] = useState<string>('');
  const [days, setDays] = useState<string>(''); // '' all time, '30', '7'
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [sort, setSort] = useState<'content' | 'audioDuration' | 'type' | 'drawing' | 'userName' | 'createdAt'>('content');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [limit] = useState<number>(100);
  const [offset, setOffset] = useState<number>(0);
  const [exhausted, setExhausted] = useState<boolean>(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${apiBase}/superadmin/workspaces/options`, { credentials: 'include' });
        if (!res.ok) throw new Error(`Failed to load workspaces: ${res.status}`);
        const data = (await res.json()) as Workspace[];
        setWorkspaces(data);
      } catch (e) {
        // non-fatal
      }
    })();
  }, [apiBase]);

  // Reset and load on filter/sort changes
  useEffect(() => {
    setRows([]);
    setOffset(0);
    setExhausted(false);
  }, [workspaceId, days, sort, order]);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const url = new URL(`${apiBase}/superadmin/analytics/comments`);
        if (workspaceId) url.searchParams.set('workspaceId', workspaceId);
        if (days) url.searchParams.set('days', days);
        if (sort) url.searchParams.set('sort', sort);
        if (order) url.searchParams.set('order', order);
        url.searchParams.set('limit', String(limit));
        url.searchParams.set('offset', String(offset));
        const res = await fetch(url.toString(), { credentials: 'include' });
        if (!res.ok) throw new Error(`Request failed: ${res.status}`);
        const data = (await res.json()) as Row[];
        setRows(prev => prev.concat(data));
        setTotal(prev => (offset === 0 ? data.length : prev + data.length));
        if (data.length < limit) setExhausted(true);
      } catch (e: any) {
        setErr(e?.message || 'Unknown error');
      } finally {
        setLoading(false);
      }
    })();
  }, [apiBase, workspaceId, days, sort, order, limit, offset]);

  // Infinite scroll: load next page when near bottom
  useEffect(() => {
    const onScroll = () => {
      if (exhausted || loading) return;
      const nearBottom = window.innerHeight + window.scrollY >= document.body.offsetHeight - 200;
      if (nearBottom) {
        setOffset(o => o + limit);
      }
    };
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, [exhausted, loading, limit]);

  if (loading) return <div>Loading…</div>;
  if (err) return <div className="text-red-600">Error: {err}</div>;

  return (
    <div>
      <h1 className="text-2xl font-bold mb-4">Comment Analytics</h1>
      <p className="mb-4 text-sm">Total Loaded: <strong>{total}</strong></p>
      <div className="mb-4 flex items-center gap-3">
        <label className="text-sm">Workspace</label>
        <select
          value={workspaceId}
          onChange={(e) => setWorkspaceId(e.target.value)}
          className="border rounded px-2 py-1 text-sm"
        >
          <option value="">All</option>
          {workspaces.map(w => (
            <option key={w.id} value={w.id}>{w.name}</option>
          ))}
        </select>
        <label className="text-sm">Time</label>
        <select
          value={days}
          onChange={(e) => setDays(e.target.value)}
          className="border rounded px-2 py-1 text-sm"
        >
          <option value="">All time</option>
          <option value="30">Last 30 days</option>
          <option value="7">Last 7 days</option>
        </select>
      </div>
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white shadow rounded overflow-hidden">
          <thead className="bg-gray-100 text-gray-700">
            <tr>
              <th className="text-left p-3 cursor-pointer select-none" onClick={() => { setSort('userName'); setOrder(o => sort === 'userName' ? (o === 'asc' ? 'desc' : 'asc') : 'asc'); }}>User</th>
              <th className="text-left p-3 cursor-pointer select-none" onClick={() => { setSort('content'); setOrder(o => sort === 'content' ? (o === 'asc' ? 'desc' : 'asc') : 'asc'); }}>Text Content</th>
              <th className="text-left p-3 cursor-pointer select-none" onClick={() => { setSort('audioDuration'); setOrder(o => sort === 'audioDuration' ? (o === 'asc' ? 'desc' : 'asc') : 'asc'); }}>Audio Content</th>
              <th className="text-left p-3">Transcript</th>
              <th className="text-left p-3 cursor-pointer select-none" onClick={() => { setSort('type'); setOrder(o => sort === 'type' ? (o === 'asc' ? 'desc' : 'asc') : 'asc'); }}>Type</th>
              <th className="text-left p-3 cursor-pointer select-none" onClick={() => { setSort('drawing'); setOrder(o => sort === 'drawing' ? (o === 'asc' ? 'desc' : 'asc') : 'asc'); }}>Drawing</th>
              <th className="text-left p-3">Tags</th>
              <th className="text-left p-3">User’s Workspaces</th>
              <th className="text-left p-3 cursor-pointer select-none" onClick={() => { setSort('createdAt'); setOrder(o => sort === 'createdAt' ? (o === 'asc' ? 'desc' : 'asc') : 'asc'); }}>Date</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => (
              <tr key={idx} className="border-b last:border-none">
                <td className="p-3">{r.userName}</td>
                <td className="p-3">{r.content ?? '–'}</td>
                <td className="p-3 font-mono text-xs break-all">{r.contentAudio ? r.contentAudio : '–'}</td>
                <td className="p-3">{r.transcript ?? '–'}</td>
                <td className="p-3">{r.type}</td>
                <td className="p-3">{r.drawingAttached}</td>
                <td className="p-3">{r.tags ?? '–'}</td>
                <td className="p-3">{r.userWorkspaces}</td>
                <td className="p-3 whitespace-nowrap">{new Date(r.createdAt).toLocaleString()}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td className="p-3" colSpan={9}>No comments found.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {loading && <div className="mt-3">Loading…</div>}
      {exhausted && rows.length > 0 && <div className="mt-3 text-sm opacity-70">End of results</div>}
    </div>
  );
};

export default AnalyticsComments;


